import React, { useState, useEffect, useCallback } from 'react';
import {
  Radio,
  RefreshCw,
  Trash2,
  Edit2,
  Plus,
  X,
  Wifi,
  WifiOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

/**
 * Readers admin page.
 *
 * Lists every RFID source that has ever fired a scan into the server
 * (USB kiosks, Wi-Fi readers, HTTP test clients) and lets the operator
 * bind each one to a controller or a locker group. The binding priority
 * is controller > group > metadata.allow_global, and is enforced by
 * db.findFreeLockerForReader on the server.
 */

const SOURCE_LABELS = { usb: 'USB kiosk', mqtt: 'Wi-Fi (MQTT)', http: 'HTTP / Test' };

const onlineDot = (lastSeen) => {
  if (!lastSeen) return 'bg-dashboard-600';
  const ageMs = Date.now() - new Date(lastSeen).getTime();
  return ageMs < 5 * 60 * 1000 ? 'bg-success-400' : 'bg-warning-400';
};

const formatAge = (timestamp) => {
  if (!timestamp) return 'never';
  const ageMs = Date.now() - new Date(timestamp).getTime();
  if (ageMs < 60 * 1000) return 'just now';
  if (ageMs < 60 * 60 * 1000) return `${Math.floor(ageMs / 60000)}m ago`;
  if (ageMs < 24 * 60 * 60 * 1000) return `${Math.floor(ageMs / 3600000)}h ago`;
  return `${Math.floor(ageMs / 86400000)}d ago`;
};

const Readers = () => {
  const [readers, setReaders] = useState([]);
  const [controllers, setControllers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    reader_id: '',
    name: '',
    source: 'mqtt',
    bindingType: 'group',
    binding: '',
    auto_assign: true
  });

  const fetchAll = useCallback(async () => {
    try {
      const [r, c, g] = await Promise.all([
        axios.get('/api/readers'),
        axios.get('/api/controllers'),
        axios.get('/api/groups')
      ]);
      setReaders(r.data?.data || []);
      setControllers(c.data?.data || []);
      setGroups(g.data || []);
    } catch (err) {
      toast.error('Failed to load readers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEdit = (reader) => {
    let bindingType = 'unbound';
    let binding = '';
    if (reader.controller_id) { bindingType = 'controller'; binding = String(reader.controller_id); }
    else if (reader.group_id) { bindingType = 'group'; binding = String(reader.group_id); }
    setEditTarget(reader);
    setEditForm({
      reader_id: reader.reader_id,
      name: reader.name || '',
      bindingType,
      binding,
      is_active: reader.is_active !== 0,
      allow_global: !!(reader.metadata && reader.metadata.allow_global),
      auto_assign: !(reader.metadata && reader.metadata.auto_assign === false)
    });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const patch = {
        name: editForm.name,
        is_active: editForm.is_active,
        controller_id: null,
        group_id: null
      };
      if (editForm.bindingType === 'controller' && editForm.binding) {
        patch.controller_id = Number(editForm.binding);
      } else if (editForm.bindingType === 'group' && editForm.binding) {
        patch.group_id = Number(editForm.binding);
      }
      patch.metadata = { ...(editTarget.metadata || {}), allow_global: !!editForm.allow_global, auto_assign: !!editForm.auto_assign };
      // Intentionally NOT patching reader_id from the edit modal: the field
      // is the device-reported identifier (e.g. ESP-RFID's `door` config),
      // changing it would orphan the row and the next scan would auto-create
      // a fresh row with the original reader_id. The modal renders it as a
      // read-only field; this guard is just belt-and-braces.
      await axios.put(`/api/readers/${editTarget.id}`, patch);
      toast.success('Reader saved');
      setEditTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save reader');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteReader = async (reader) => {
    if (!window.confirm(`Forget reader "${reader.reader_id}"? It will be re-created on the next scan.`)) return;
    try {
      await axios.delete(`/api/readers/${reader.id}`);
      toast.success('Reader removed');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const createReader = async () => {
    if (!createForm.reader_id) {
      toast.error('reader_id is required');
      return;
    }
    try {
      const body = {
        reader_id: createForm.reader_id,
        name: createForm.name || null,
        source: createForm.source
      };
      if (createForm.bindingType === 'controller' && createForm.binding) body.controller_id = Number(createForm.binding);
      else if (createForm.bindingType === 'group' && createForm.binding) body.group_id = Number(createForm.binding);
      body.metadata = { auto_assign: createForm.auto_assign !== false };
      await axios.post('/api/readers', body);
      toast.success('Reader created');
      setCreateOpen(false);
      setCreateForm({ reader_id: '', name: '', source: 'mqtt', bindingType: 'group', binding: '', auto_assign: true });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create reader');
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-gray-300 flex items-center space-x-2">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Loading readers...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center space-x-2">
            <Radio className="w-6 h-6" />
            <span>Readers</span>
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            RFID scan sources. Each reader is bound to a controller or a
            locker group; bindings are evaluated in that order when a scan
            looks for a free locker.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchAll}
            className="flex items-center space-x-2 bg-dashboard-700 hover:bg-dashboard-600 text-white px-3 py-2 rounded-lg transition"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-500 text-white px-3 py-2 rounded-lg transition"
          >
            <Plus className="w-4 h-4" />
            <span>New reader</span>
          </button>
        </div>
      </div>

      {readers.length === 0 ? (
        <div className="border border-dashboard-700 rounded-xl p-6 text-center text-gray-400 bg-dashboard-800">
          No readers yet. They auto-register on the first MQTT scan to
          <code className="text-primary-400 mx-1">rfid/&lt;reader_id&gt;/scan</code>
          or HTTP <code className="text-primary-400">POST /api/rfid/scan</code>.
        </div>
      ) : (
        <div className="space-y-3">
          {readers.map((reader) => (
            <div
              key={reader.id}
              className={`bg-dashboard-800 border rounded-xl p-4 ${
                reader.is_active ? 'border-dashboard-700' : 'border-danger-600/40 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-600/20 border border-primary-600 rounded-lg flex items-center justify-center">
                    <Radio className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-semibold">{reader.name || reader.reader_id}</span>
                      <span className={`inline-block w-2 h-2 rounded-full ${onlineDot(reader.last_seen)}`} />
                      {reader.is_active ? (
                        <Wifi className="w-3.5 h-3.5 text-success-400" />
                      ) : (
                        <WifiOff className="w-3.5 h-3.5 text-danger-400" />
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 flex items-center space-x-2">
                      <span className="font-mono">{reader.reader_id}</span>
                      <span>·</span>
                      <span>{SOURCE_LABELS[reader.source] || reader.source}</span>
                      <span>·</span>
                      <span>last seen {formatAge(reader.last_seen)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <BindingBadge reader={reader} />
                  {reader.metadata && reader.metadata.auto_assign === false && (
                    <span className="text-xs px-2 py-0.5 rounded bg-danger-600/20 text-danger-300 border border-danger-600/40">
                      Auto-assign off
                    </span>
                  )}
                  <button
                    onClick={() => openEdit(reader)}
                    title="Edit reader"
                    className="text-gray-400 hover:text-primary-400 p-1"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteReader(reader)}
                    title="Forget reader"
                    className="text-gray-400 hover:text-danger-400 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <ReaderModal
          title={`Edit ${editTarget.reader_id}`}
          form={editForm}
          setForm={setEditForm}
          controllers={controllers}
          groups={groups}
          onClose={() => setEditTarget(null)}
          onSave={saveEdit}
          saving={editSaving}
          showActiveToggle
        />
      )}

      {/* Create modal */}
      {createOpen && (
        <ReaderModal
          title="New reader"
          form={createForm}
          setForm={setCreateForm}
          controllers={controllers}
          groups={groups}
          onClose={() => setCreateOpen(false)}
          onSave={createReader}
          saving={false}
          showReaderIdInput
        />
      )}
    </div>
  );
};

const BindingBadge = ({ reader }) => {
  if (reader.controller) {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-primary-600/20 text-primary-300 border border-primary-600/40">
        Controller: {reader.controller.hostname}
      </span>
    );
  }
  if (reader.group) {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-cyan-600/20 text-cyan-300 border border-cyan-600/40">
        Group: {reader.group.name}
      </span>
    );
  }
  if (reader.metadata && reader.metadata.allow_global) {
    return <span className="text-xs px-2 py-0.5 rounded bg-warning-600/20 text-warning-300 border border-warning-600/40">Global fallback</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded bg-dashboard-700 text-gray-400 border border-dashboard-600">Unbound</span>;
};

const ReaderModal = ({ title, form, setForm, controllers, groups, onClose, onSave, saving, showActiveToggle, showReaderIdInput }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
    <div className="bg-dashboard-800 border border-dashboard-700 rounded-xl shadow-card w-full max-w-md p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      {showReaderIdInput ? (
        <>
          <label className="block text-xs text-gray-400 mb-1">
            Reader ID <span className="text-gray-500">(must match the device's reported identifier — for ESP-RFID this is the "Door Name" config field)</span>
          </label>
          <input
            type="text"
            value={form.reader_id || ''}
            onChange={(e) => setForm((f) => ({ ...f, reader_id: e.target.value }))}
            className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3 font-mono text-sm"
            placeholder="F1-2"
          />
        </>
      ) : (
        <>
          <label className="block text-xs text-gray-400 mb-1">
            Reader ID <span className="text-gray-500">(read-only — set by the device firmware)</span>
          </label>
          <div className="w-full bg-dashboard-900/60 border border-dashboard-700 rounded-lg px-3 py-2 text-gray-300 mb-3 font-mono text-sm cursor-not-allowed select-all">
            {form.reader_id || '-'}
          </div>
        </>
      )}

      <label className="block text-xs text-gray-400 mb-1">Display name</label>
      <input
        type="text"
        value={form.name || ''}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3"
        placeholder="Women's lockerroom reader"
      />

      <label className="block text-xs text-gray-400 mb-1">Binding type</label>
      <select
        value={form.bindingType || 'group'}
        onChange={(e) => setForm((f) => ({ ...f, bindingType: e.target.value, binding: '' }))}
        className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3"
      >
        <option value="controller">Controller (single Aywana board)</option>
        <option value="group">Locker group</option>
        <option value="unbound">Unbound (no auto-allocation)</option>
      </select>

      {form.bindingType === 'controller' && (
        <select
          value={form.binding || ''}
          onChange={(e) => setForm((f) => ({ ...f, binding: e.target.value }))}
          className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3"
        >
          <option value="">-- pick a controller --</option>
          {controllers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.hostname}{c.group ? ` (${c.group.name})` : ''}
            </option>
          ))}
        </select>
      )}

      {form.bindingType === 'group' && (
        <select
          value={form.binding || ''}
          onChange={(e) => setForm((f) => ({ ...f, binding: e.target.value }))}
          className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3"
        >
          <option value="">-- pick a group --</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      )}

      <label className="flex items-center space-x-2 mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={form.auto_assign !== false}
          onChange={(e) => setForm((f) => ({ ...f, auto_assign: e.target.checked }))}
        />
        <span className="text-sm text-gray-300">
          Auto-assign new cards (uncheck so unassigned cards are rejected instead of grabbing a free locker)
        </span>
      </label>

      <label className="flex items-center space-x-2 mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.allow_global}
          onChange={(e) => setForm((f) => ({ ...f, allow_global: e.target.checked }))}
        />
        <span className="text-sm text-gray-300">
          Allow global fallback (allocate any free locker if the binding has none)
        </span>
      </label>

      {showActiveToggle && (
        <label className="flex items-center space-x-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active !== false}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
          <span className="text-sm text-gray-300">Active (uncheck to disable scans from this reader)</span>
        </label>
      )}

      <div className="flex justify-end space-x-2">
        <button onClick={onClose} className="px-3 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
        <button
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  </div>
);

export default Readers;
